import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, ArrowUpRight } from "lucide-react";
import { PageFlip } from "page-flip";
import { useDocumentMeta } from "../hooks/use-document-meta";

// Catalogue Frites Dorées feuilletable (version octobre 2026).
// Pages pré-rendues en WebP dans /public/catalogue-2026/page-XX.webp.
// Effet de page réaliste (courbure, pli, ombres) via la librairie page-flip
// (MIT, ~40 Ko). Double page sur ordinateur, page simple sur mobile ;
// on tourne en cliquant, en glissant le coin, au clavier ou avec les flèches.
const TOTAL = 76;
const src = (n) => `/catalogue-2026/page-${String(n).padStart(2, "0")}.webp`;

export default function CatalogueFlip() {
  useDocumentMeta({
    title: "Catalogue Frites Dorées 2026 — feuilletez notre offre grossiste",
    description:
      "Feuilletez en ligne le catalogue Frites Dorées (octobre 2026) : frites, viandes, volailles, sauces, pains, desserts, emballages. Grossiste B2B fast-food Île-de-France.",
    canonicalPath: "/catalogue-feuilletable",
    robots: "index",
  });

  const host = useRef(null); // conteneur géré par page-flip (hors React)
  const stage = useRef(null);
  const pf = useRef(null);
  const loader = useRef(() => {});
  const [index, setIndex] = useState(0);
  const [portrait, setPortrait] = useState(false);
  const [full, setFull] = useState(false);

  useEffect(() => {
    const wrap = host.current;
    if (!wrap) return undefined;
    // page-flip déplace et masque les pages lui-même : on construit son DOM
    // à la main, en dehors de l'arbre React, pour éviter tout conflit.
    const book = document.createElement("div");
    book.className = "fd-book";
    const imgs = [];
    for (let n = 1; n <= TOTAL; n++) {
      const page = document.createElement("div");
      page.className = "fd-page";
      if (n === 1 || n === TOTAL) page.setAttribute("data-density", "hard");
      const img = document.createElement("img");
      img.alt = `Catalogue Frites Dorées — page ${n}`;
      img.decoding = "async";
      img.draggable = false;
      // Les premières pages chargent tout de suite, les autres à l'approche.
      if (n <= 12) img.src = src(n);
      else img.dataset.src = src(n);
      page.appendChild(img);
      imgs.push(img);
      book.appendChild(page);
    }
    wrap.appendChild(book);

    // page-flip réorganise les pages dans le DOM : on garde la liste des
    // images à part et on précharge large autour de la page courante.
    const load = (i) => {
      for (let k = Math.max(0, i - 4); k <= Math.min(TOTAL - 1, i + 10); k++) {
        const img = imgs[k];
        if (img && img.dataset.src) {
          img.src = img.dataset.src;
          delete img.dataset.src;
        }
      }
    };
    loader.current = load;

    const flip = new PageFlip(book, {
      width: 595,
      height: 842,
      size: "stretch",
      minWidth: 260,
      maxWidth: 900,
      minHeight: 368,
      maxHeight: 1273,
      showCover: true,
      usePortrait: true,
      autoSize: true,
      drawShadow: true,
      maxShadowOpacity: 0.55,
      flippingTime: 850,
      mobileScrollSupport: true,
      swipeDistance: 30,
      showPageCorners: true,
      startZIndex: 2,
    });
    flip.loadFromHTML(book.querySelectorAll(".fd-page"));
    flip.on("changeState", () => load(flip.getCurrentPageIndex()));
    flip.on("flip", (e) => {
      setIndex(e.data);
      load(e.data);
    });
    flip.on("changeOrientation", (e) => setPortrait(e.data === "portrait"));
    setPortrait(flip.getOrientation() === "portrait");
    load(0);
    pf.current = flip;

    return () => {
      try {
        flip.destroy();
      } catch (err) {
        /* déjà détruit */
      }
      pf.current = null;
      while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    };
  }, []);

  useEffect(() => {
    const on = (e) => {
      if (!pf.current) return;
      if (e.key === "ArrowRight") pf.current.flipNext();
      if (e.key === "ArrowLeft") pf.current.flipPrev();
      if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => pf.current?.update(), 60);
    return () => clearTimeout(t);
  }, [full]);

  const goTo = (n) => {
    const i = Math.max(0, Math.min(TOTAL - 1, n - 1));
    const f = pf.current;
    if (!f) return;
    loader.current(i);
    // Saut proche : on anime ; saut lointain : on va directement à la page.
    if (Math.abs(i - f.getCurrentPageIndex()) <= 2) f.flip(i);
    else f.turnToPage(i);
    loader.current(i);
    setIndex(i);
  };

  const shown = index + 1;
  const pair = !portrait && index > 0 && index < TOTAL - 1;
  const left = pair ? (index % 2 === 1 ? index + 1 : index) : shown;
  const label = pair ? `${left}–${Math.min(left + 1, TOTAL)}` : `${shown}`;

  return (
    <main className={`bg-[#050505] min-h-screen ${full ? "" : "pt-28 md:pt-32"}`} data-testid="catalogue-flip-page">
      {!full && (
        <header className="px-6 md:px-10 lg:px-14 max-w-7xl mx-auto mb-8 md:mb-10">
          <div className="tag-label mb-4 flex items-center gap-3">
            <span className="w-10 h-px bg-[#FFC300]"></span>
            <span>Version octobre 2026 · {TOTAL} pages</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
              Notre catalogue <span className="text-[#FFC300]">grossiste</span>
            </h1>
            <Link to="/contact" className="btn-secondary self-start md:self-auto">
              Demander un devis <ArrowUpRight size={16} />
            </Link>
          </div>
        </header>
      )}

      <section
        ref={stage}
        className={`relative mx-auto ${full ? "fixed inset-0 z-[60] bg-[#050505] flex flex-col justify-center px-4 py-6" : "px-4 md:px-10 lg:px-14 max-w-7xl"}`}
        aria-label="Catalogue feuilletable"
      >
        <div className="relative fd-stage rounded-2xl px-3 py-6 sm:px-14 sm:py-10 overflow-hidden">
          <div
            ref={host}
            className="fd-book-host mx-auto"
            style={{ maxWidth: full ? "min(1500px, 96vw, calc((100vh - 130px) * 1.41))" : "min(1100px, calc((100vh - 230px) * 1.41))" }}
          />
          <button
            type="button"
            onClick={() => pf.current?.flipPrev()}
            disabled={index === 0}
            aria-label="Page précédente"
            className="absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/70 border border-white/10 text-white flex items-center justify-center hover:bg-[#FFC300] hover:text-black disabled:opacity-20 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={() => pf.current?.flipNext()}
            disabled={index >= TOTAL - 1}
            aria-label="Page suivante"
            className="absolute right-1 sm:right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/70 border border-white/10 text-white flex items-center justify-center hover:bg-[#FFC300] hover:text-black disabled:opacity-20 disabled:pointer-events-none transition-colors"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="mt-5 flex items-center justify-between gap-4">
          <p className="font-mono text-xs text-zinc-400 tabular-nums" aria-live="polite">
            Page {label} / {TOTAL}
          </p>
          <input
            type="range"
            min={1}
            max={TOTAL}
            value={shown}
            onChange={(e) => goTo(Number(e.target.value))}
            aria-label="Aller à la page"
            className="flex-1 max-w-md accent-[#FFC300]"
          />
          <button
            type="button"
            onClick={() => setFull((f) => !f)}
            className="tag-label flex items-center gap-2 text-zinc-300 hover:text-[#FFC300] transition-colors"
          >
            {full ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span className="hidden sm:inline">{full ? "Réduire" : "Plein écran"}</span>
          </button>
        </div>
        <p className="mt-3 text-[11px] text-zinc-500">Astuce : tirez le coin d'une page pour la tourner, ou utilisez les flèches du clavier.</p>
      </section>

      {!full && (
        <section className="px-6 md:px-10 lg:px-14 max-w-7xl mx-auto py-14">
          <p className="tag-label text-[#FFC300] mb-5">Toutes les pages</p>
          <div className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-12 gap-2">
            {Array.from({ length: TOTAL }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  goTo(n);
                  stage.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className={`relative aspect-[595/842] overflow-hidden rounded-sm border transition-colors ${
                  n === shown || (pair && (n === left || n === left + 1)) ? "border-[#FFC300]" : "border-white/10 hover:border-white/40"
                }`}
                aria-label={`Aller à la page ${n}`}
              >
                <img src={src(n)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] font-mono text-zinc-300 py-0.5">{n}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
